import type { FC } from 'react';

import { Divider } from '../divider';
import { ModalBackdrop } from './modal-backdrop';
import { ModalBody } from './modal-body';
import { ModalClose } from './modal-close';
import { ModalCloseButton } from './modal-close-button';
import { ModalContent } from './modal-content';
import { ModalFooter } from './modal-footer';
import { ModalHeader } from './modal-header';
import { ModalPortal } from './modal-portal';
import { ModalRoot } from './modal-root';
import { ModalTrigger } from './modal-trigger';
import type { ModalPropsInterface } from './types/modal.types';

/**
 * A modal dialog component, also commonly know simply as a "modal" or a "lightbox".
 *
 * @since 5.0.0
 */
export const Modal: FC<ModalPropsInterface> & {
  Root: typeof ModalRoot;
  Trigger: typeof ModalTrigger;
  Portal: typeof ModalPortal;
  Backdrop: typeof ModalBackdrop;
  Content: typeof ModalContent;
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
  CloseButton: typeof ModalCloseButton;
  Close: typeof ModalClose;
} = (props: ModalPropsInterface) => {
  const {
    children,
    dividers = true,
    trigger,
    header,
    body,
    footer,
    closeButton,
    css: cssProp,
    style,
    className,
    portalId,
    portalRoot,
    portalPreserveTabOrder,
    ...rest
  } = props;
  return (
    <Modal.Root {...rest}>
      <Modal.Trigger>{trigger}</Modal.Trigger>
      <Modal.Portal id={portalId} root={portalRoot} preserveTabOrder={portalPreserveTabOrder}>
        <Modal.Backdrop>
          <Modal.Content css={cssProp} style={style} className={className}>
            <Modal.CloseButton>{closeButton}</Modal.CloseButton>
            {header ? <Modal.Header>{header}</Modal.Header> : null}
            {dividers ? <Divider /> : null}
            {body ? <Modal.Body>{body}</Modal.Body> : null}
            {children}
            {footer && dividers ? <Divider /> : null}
            {footer ? <Modal.Footer>{footer}</Modal.Footer> : null}
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

Modal.displayName = 'Modal';

Modal.Root = ModalRoot;
Modal.Trigger = ModalTrigger;
Modal.Portal = ModalPortal;
Modal.Backdrop = ModalBackdrop;
Modal.Content = ModalContent;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.CloseButton = ModalCloseButton;
Modal.Close = ModalClose;
